import React, { useState, useEffect, useCallback } from "react";
import PlugManagementSection from "./components/workspace/PlugManagementSection";
import PlugAnalyticsSection from "./components/workspace/PlugAnalyticsSection";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GRN = "#1D9E75";
const AMB = "#F5A623";
const RED = "#D85A30";
const BG = "#f3f2ef";
const CARD = "#FFFFFF";
const BORDER = "rgba(0,0,0,0.1)";

const ROLE_STYLE = {
  full_admin:    { bg: "#EEEDFE", color: "#3C3489", label: "Super Admin" },
  team_admin:    { bg: "#EEEDFE", color: "#3C3489", label: "Workgroup Admin" },
  billing_admin: { bg: "#E6F1FB", color: "#0C447C", label: "Billing Admin" },
  member:        { bg: "#F0F0F0", color: "#555555", label: "Member" },
};

const AVATAR_COLORS = ["#0077B5","#1D9E75","#7C3AED","#D85A30","#F5A623","#059669","#DC2626","#7C3AED"];

function getAvatarColor(name) {
  if (!name) return "#888";
  return AVATAR_COLORS[(name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length];
}

function Avatar({ name, src, size = 36 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  if (src && !err) {
    return <img src={src} alt={name} onError={() => setErr(true)} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: getAvatarColor(name), display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 600, color: "#fff", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function RoleBadge({ role }) {
  const s = ROLE_STYLE[role] || ROLE_STYLE.member;
  return <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function StatCard({ label, value, sub, subColor = "#888" }) {
  return (
    <div style={{ background: BG, borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500, marginBottom: sub ? 4 : 0 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: subColor }}>{sub}</div>}
    </div>
  );
}

function fmtNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n);
}

function BackButton({ label, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "transparent", border: "none", color: G, fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: "0 0 12px", display: "flex", alignItems: "center", gap: 4 }}>
      &larr; {label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE 1 — TEAM OVERVIEW (Super Admin sees workgroups)
   ═══════════════════════════════════════════════════════════════════════════ */

function InviteActions({ invId, workspaceId, onDone }) {
  const [acting, setActing] = useState(false);
  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const handleResend = async () => {
    setActing(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/invite/resend`, {
        method: "POST", headers, body: JSON.stringify({ invitation_id: invId }),
      });
      const d = await r.json();
      if (r.ok) { alert("Invitation re-sent"); onDone(); }
      else alert(d.error || "Failed to resend");
    } catch { alert("Network error"); }
    setActing(false);
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this invitation? The pending seat charge will be released.")) return;
    setActing(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/invite/cancel`, {
        method: "POST", headers, body: JSON.stringify({ invitation_id: invId }),
      });
      const d = await r.json();
      if (r.ok) { onDone(); }
      else alert(d.error || "Failed to cancel");
    } catch { alert("Network error"); }
    setActing(false);
  };

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <button onClick={handleResend} disabled={acting} style={{ background: "transparent", color: G, border: "1px solid " + G, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", opacity: acting ? 0.5 : 1 }}>
        Re-invite
      </button>
      <button onClick={handleCancel} disabled={acting} style={{ background: "transparent", color: RED, border: "1px solid " + RED, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", opacity: acting ? 0.5 : 1 }}>
        Cancel
      </button>
    </div>
  );
}

function TeamOverview({ data, workspaceId, onOpenWorkgroup, onCreateWorkgroup, onDeleteWorkgroup, onTransferOwnership, onReload }) {
  const ws = data.workspace;
  const members = data.members || [];
  const workgroups = data.workgroups || [{ id: null, name: ws.name, member_count: members.length }];
  const totalImpr = data.total_impressions_30d || 0;
  const isAdmin = data.caller_role === "full_admin";

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(ws.name);
  const [editingWgId, setEditingWgId] = useState(null);
  const [editingWgName, setEditingWgName] = useState("");

  const saveWgRename = async (wgId) => {
    const trimmed = (editingWgName || "").trim();
    if (!trimmed) {
      setEditingWgId(null);
      setEditingWgName("");
      return;
    }
    try {
      const token = localStorage.getItem("nectar_st");
      const res = await fetch(`${API_BASE}/app/api/workspace/${wgId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Session-Token": token },
        body: JSON.stringify({ name: trimmed }),
      });
      if (res.ok) {
        setEditingWgId(null);
        setEditingWgName("");
        if (onReload) onReload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Failed to rename workgroup");
        setEditingWgId(null);
        setEditingWgName("");
      }
    } catch {
      alert("Failed to rename workgroup");
      setEditingWgId(null);
      setEditingWgName("");
    }
  };

  const saveRename = async () => {
    const trimmed = (nameValue || "").trim();
    if (!trimmed || trimmed === ws.name) {
      setNameValue(ws.name);
      setEditingName(false);
      return;
    }
    try {
      const token = localStorage.getItem("nectar_st");
      const res = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Session-Token": token },
        body: JSON.stringify({ name: trimmed }),
      });
      if (res.ok) {
        setNameValue(trimmed);
        ws.name = trimmed;
        setEditingName(false);
        if (onReload) onReload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Failed to rename workspace");
        setNameValue(ws.name);
        setEditingName(false);
      }
    } catch {
      alert("Failed to rename workspace");
      setNameValue(ws.name);
      setEditingName(false);
    }
  };

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <div style={{ marginBottom: 28 }}>
        {editingName ? (
          <input
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveRename()}
            onBlur={saveRename}
            autoFocus
            maxLength={80}
            style={{ fontSize: 12, color: "#999", marginBottom: 2, border: "1px solid " + BORDER, borderRadius: 6, padding: "2px 6px", fontFamily: "inherit", outline: "none", width: 260 }}
          />
        ) : (
          <div style={{ fontSize: 12, color: "#999", marginBottom: 2 }}>
            {ws.name}
            {isAdmin && !editingName && (
              <button
                onClick={() => setEditingName(true)}
                style={{
                  marginLeft: 10, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                  color: "#0077B5", background: "transparent", border: "1px solid #0077B5",
                  borderRadius: 4, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Edit name
              </button>
            )}
          </div>
        )}
        <div style={{ fontSize: 22, fontWeight: 500 }}>Team overview</div>
      </div>

      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
        <StatCard label="Total members" value={members.length} />
        <StatCard label="Posts this week" value={data.total_posts_this_week || 0} sub={(data.total_approved_this_week || 0) + " approved"} subColor={GRN} />
        <StatCard label="Est. impressions (30d)" value={fmtNum(totalImpr)} />
      </div>

      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Workgroups</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {workgroups.map(wg => (
            <div
              key={wg.id || "primary"}
              style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: editingWgId === wg.id ? "default" : "pointer" }}
              onClick={() => { if (editingWgId !== wg.id) onOpenWorkgroup(wg.id); }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span style={{ fontSize: 20 }}>{wg.is_lobby === true ? "\uD83C\uDFE0" : wg.is_default === true ? "\u2605" : "\uD83D\uDCC1"}</span>
                <div>
                  {editingWgId === wg.id && wg.is_lobby !== true && wg.is_default !== true ? (
                    <input
                      value={editingWgName}
                      onChange={(e) => setEditingWgName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveWgRename(wg.id); if (e.key === "Escape") { setEditingWgId(null); setEditingWgName(""); } }}
                      onBlur={() => saveWgRename(wg.id)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      maxLength={80}
                      style={{ fontSize: 15, fontWeight: 500, border: "1px solid " + BORDER, borderRadius: 6, padding: "2px 6px", fontFamily: "inherit", outline: "none", width: 200 }}
                    />
                  ) : (
                    <div style={{ fontSize: 15, fontWeight: 500 }}>
                      {wg.name}
                      {(wg.is_default === true || wg.is_lobby === true) && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: "#6B7280", fontWeight: 600, background: "#F3F4F6", borderRadius: 4, padding: "1px 6px" }}>System</span>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {wg.member_count} member{wg.member_count !== 1 ? "s" : ""}
                    {wg.is_active === false && wg.member_count === 0 && <span style={{ marginLeft: 6, color: "#92400E" }}>Paused</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isAdmin && wg.id && editingWgId !== wg.id && wg.is_lobby !== true && wg.is_default !== true && (
                  <button onClick={(e) => { e.stopPropagation(); setEditingWgId(wg.id); setEditingWgName(wg.name); }}
                    style={{ background: "transparent", color: G, border: "none", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, whiteSpace: "nowrap", padding: "4px 8px" }}>
                    Rename
                  </button>
                )}
                {wg.id && wg.member_count <= 1 && wg.is_default !== true && wg.is_lobby !== true && (
                  <button onClick={(e) => { e.stopPropagation(); onDeleteWorkgroup(wg); }}
                    style={{ background: "transparent", color: RED, border: "none", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, whiteSpace: "nowrap", padding: "4px 8px" }}>
                    Delete
                  </button>
                )}
                <button style={{ background: "transparent", color: G, border: "none", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, whiteSpace: "nowrap" }}>
                  Open →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending invites */}
      {(data.pending_invites || []).length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Pending invites ({data.pending_invites.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.pending_invites.map(inv => (
              <div key={inv.id} style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>✉</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{inv.email}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                      {inv.role || "member"} · Expires {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "soon"}
                    </div>
                  </div>
                </div>
                <InviteActions invId={inv.id} workspaceId={workspaceId} onDone={onReload} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
        <button onClick={onCreateWorkgroup} style={{ background: "transparent", color: G, border: "1.5px solid " + G, borderRadius: 8, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
          + Create workgroup
        </button>
        {data.current_user_id === data.owner_user_id && (
          <button
            onClick={onTransferOwnership}
            style={{
              padding: "6px 14px", fontSize: 13, fontWeight: 600,
              backgroundColor: "transparent", color: "#0077B5",
              border: "1px solid #0077B5", borderRadius: 6, cursor: "pointer",
            }}
          >
            Transfer ownership
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE 2 — WORKGROUP MANAGEMENT
   ═══════════════════════════════════════════════════════════════════════════ */

function WorkgroupView({ data, workspaceId, onBack, onViewMember, onInvite, onBulkInvite, onReload }) {
  const ws = data.workspace;
  const members = data.members || [];
  const activeMembers = members.filter(m => m.status === "active").length;
  const otherWorkgroups = (data.workgroups || []).filter(wg => wg.id && wg.id !== workspaceId);
  const isAdmin = data.caller_role === "full_admin";
  const canTransfer = isAdmin && otherWorkgroups.length > 0;
  const [transferTarget, setTransferTarget] = useState(null); // {user_id, name}
  const [transferDest, setTransferDest] = useState("");
  const [transferring, setTransferring] = useState(false);

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <BackButton label="Back to Team overview" onClick={onBack} />

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>{ws.name}</div>
        </div>
        <button onClick={onInvite} style={{ background: "transparent", color: G, border: "1.5px solid " + G, borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
          + Invite member
        </button>
      </div>

      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
        <StatCard label="Members" value={activeMembers} sub="active" subColor={GRN} />
        <StatCard label="Posts this week" value={data.total_posts_this_week || 0} />
        <StatCard label="Est. impressions (30d)" value={fmtNum(data.total_impressions_30d || 0)} />
      </div>

      {/* Team plugs — primary admin workflow, above member list */}
      <div id="plug-management-section">
        <PlugManagementSection workspaceId={workspaceId} />
      </div>
      <PlugAnalyticsSection workspaceId={workspaceId} />

      {/* Owner banner (when owner is not a member/seat) */}
      {data.owner_info && (
        <div style={{ background: "#FFF8ED", border: "1px solid #F5A623", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={data.owner_info.name} src={data.owner_info.avatar} size={32} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{data.owner_info.name}</span>
            <span style={{ marginLeft: 8, fontSize: 11, background: AMB, color: "#fff", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>Owner</span>
            {data.owner_info.email && <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>{data.owner_info.email}</div>}
          </div>
          <div style={{ fontSize: 11, color: "#92400E" }}>Does not use a seat</div>
        </div>
      )}

      {/* Members table */}
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 80px 80px", gap: 0, padding: "10px 16px", background: BG, borderBottom: "0.5px solid " + BORDER }}>
          {["NAME","ROLE","POSTS","VIEWS","ACTION"].map((h, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</div>
          ))}
        </div>
        {members.map((m, i) => (
          <div key={m.user_id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 80px 80px", gap: 0, padding: "12px 16px", borderBottom: i < members.length - 1 ? "0.5px solid " + BORDER : "none", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={m.name} src={m.avatar} size={36} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{m.email}</div>
                {m.account_status && m.account_status !== "active" && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#DC2626", background: "#FEF2F2", padding: "1px 6px", borderRadius: 4, marginTop: 2, display: "inline-block" }}>
                    {m.account_status.charAt(0).toUpperCase() + m.account_status.slice(1)}
                  </span>
                )}
              </div>
            </div>
            <div><RoleBadge role={m.role} /></div>
            <div style={{ fontSize: 13 }}>{m.posts_this_week || 0}</div>
            <div style={{ fontSize: 13 }}>{fmtNum(m.est_impressions_30d || 0)}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {m.user_id === data.current_user_id ? (
                <span style={{ fontSize: 12, color: "#888" }}>You</span>
              ) : (
                <>
                  <button onClick={() => onViewMember(m)} style={{ background: "transparent", color: G, border: "none", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0, fontWeight: 500 }}>View</button>
                  {canTransfer && m.role !== "full_admin" && (
                    <button onClick={() => { setTransferTarget({ user_id: m.user_id, name: m.name }); setTransferDest(otherWorkgroups[0]?.id || ""); }} style={{ background: "transparent", color: "#888", border: "none", fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>Move</button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Transfer confirmation */}
      {transferTarget && (
        <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "16px 20px", marginBottom: 16, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#92400E", marginBottom: 10 }}>Move {transferTarget.name}</div>
          <div style={{ fontSize: 13, color: "#92400E", marginBottom: 12 }}>Their role will reset to member in the destination workgroup. Pending posts stay with them.</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select value={transferDest} onChange={e => setTransferDest(e.target.value)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, minWidth: 200 }}>
              {otherWorkgroups.map(wg => <option key={wg.id} value={wg.id}>{wg.name}</option>)}
            </select>
            <button disabled={transferring || !transferDest} onClick={async () => {
              if (!window.confirm(`Move ${transferTarget.name} to ${otherWorkgroups.find(w => w.id === transferDest)?.name || "selected workgroup"}? Their role will reset to member.`)) return;
              setTransferring(true);
              try {
                const st = localStorage.getItem("nectar_st");
                const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/members/${transferTarget.user_id}/transfer`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", "X-Session-Token": st },
                  body: JSON.stringify({ target_workspace_id: transferDest }),
                });
                const d = await r.json();
                if (r.ok && d.transferred) {
                  if (d.billing_warning) alert("Transfer complete. Note: " + d.billing_warning);
                  setTransferTarget(null);
                  onReload();
                } else {
                  alert(d.error || "Transfer failed");
                }
              } catch { alert("Network error"); }
              setTransferring(false);
            }} style={{ background: "#D97706", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: transferring ? 0.6 : 1 }}>
              {transferring ? "Moving..." : "Confirm move"}
            </button>
            <button onClick={() => setTransferTarget(null)} style={{ background: "transparent", color: "#666", border: "1px solid #ddd", borderRadius: 6, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Pending invites */}
      {(data.pending_invites || []).length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: "#666" }}>Pending invites ({data.pending_invites.length})</div>
          <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, overflow: "hidden" }}>
            {data.pending_invites.map((inv, i) => (
              <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < data.pending_invites.length - 1 ? "0.5px solid " + BORDER : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#f3f2ef", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#888" }}>
                    ✉
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{inv.email}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                      {inv.role || "member"} · Expires {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "soon"}
                    </div>
                  </div>
                </div>
                <InviteActions invId={inv.id} workspaceId={workspaceId} onDone={onReload} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk invite CSV — docs collapsed */}
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: "20px 24px", marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Bulk invite from CSV</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Upload a CSV file to invite multiple members at once</div>
          </div>
          <button onClick={onBulkInvite} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
            Upload CSV
          </button>
        </div>
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 600, color: G, fontSize: 13 }}>How does Bulk invite from CSV work?</summary>
          <div style={{ background: "#f8f8f6", borderRadius: 8, padding: "12px 16px", fontSize: 12, color: "#666", lineHeight: 1.7, fontFamily: "monospace", marginTop: 10 }}>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 500, color: "#333", marginBottom: 6, fontSize: 12 }}>Required columns:</div>
            <div>email, first_name, last_name, role</div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", color: "#999", marginTop: 8, fontSize: 11, lineHeight: 1.5 }}>
              <strong>email</strong> (required) — <strong>first_name</strong>, <strong>last_name</strong> (optional) — <strong>role</strong>: member or billing_admin (defaults to member)<br />
              Example: jane@company.com, Jane, Smith, member<br />
              Header row is auto-detected. One person per row.
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE 3 — MEMBER DETAIL
   ═══════════════════════════════════════════════════════════════════════════ */

function MemberDetail({ member, workspaceId, workspaceName, onBack, onReload }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(member.role);
  const [breakoff, setBreakoff] = useState("none");
  const [savingRole, setSavingRole] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/app/api/workspace/${workspaceId}/members/${member.user_id}/detail`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setDetail(d); setRole(d.role); } setLoading(false); })
      .catch(() => setLoading(false));
  }, [member.user_id, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRole = async () => {
    if (role === detail?.role) return;
    setSavingRole(true);
    try {
      await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/members/${member.user_id}/role`, {
        method: "PATCH", headers, body: JSON.stringify({ role }),
      });
      onReload();
    } catch { alert("Error saving role"); }
    setSavingRole(false);
  };

  const handleSuspend = async () => {
    if (!window.confirm(`Suspend ${detail?.name || member.name}?`)) return;
    setSuspending(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/members/${member.user_id}/suspend`, {
        method: "POST", headers,
      });
      if (r.ok) { onReload(); onBack(); }
      else { const d = await r.json(); alert(d.error || "Failed to suspend"); }
    } catch { alert("Error suspending member"); }
    setSuspending(false);
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${detail?.name || member.name} from the workgroup? Their posts will stop.`)) return;
    setRemoving(true);
    try {
      await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/members/${member.user_id}/remove`, {
        method: "POST", headers,
      });
      onReload(); onBack();
    } catch { alert("Error removing member"); }
    setRemoving(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading member...</div>;
  if (!detail) return <div style={{ padding: 40, textAlign: "center", color: RED }}>Failed to load member</div>;

  const plugLabel = detail.plug_status === "personalized" ? "Personalized ✓"
    : detail.plug_status === "ai_rewrite" ? "Using AI rewrite"
    : detail.plug_status === "deleted" ? "Deleted ⚠️"
    : "No plug";

  const plugColor = detail.plug_status === "personalized" ? GRN
    : detail.plug_status === "deleted" ? RED : "#888";

  const joinedDate = detail.joined_at ? new Date(detail.joined_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  const monthKeys = Object.keys(detail.monthly_posts || {}).sort().reverse();

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <BackButton label={`Back to ${workspaceName}`} onClick={onBack} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
        <Avatar name={detail.name} src={detail.avatar} size={56} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>{detail.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <RoleBadge role={detail.role} />
            {joinedDate && <span style={{ fontSize: 12, color: "#888" }}>Joined {joinedDate}</span>}
            {detail.account_status && detail.account_status !== "active" && (
              <span style={{ fontSize: 11, fontWeight: 600, color: "#DC2626", background: "#FEF2F2", padding: "2px 8px", borderRadius: 4 }}>
                {detail.account_status.charAt(0).toUpperCase() + detail.account_status.slice(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
        <StatCard label="Posts this week" value={detail.posts_this_week} />
        <StatCard label="Views (30d)" value={fmtNum(detail.views_30d)} />
        <StatCard label="Clicks (30d)" value={fmtNum(detail.clicks_30d)} />
      </div>

      {/* Role section */}
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Role</div>
        <select value={role} onChange={e => setRole(e.target.value)} style={{ width: "100%", maxWidth: 320, padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14, marginBottom: 8 }}>
          <option value="member">Member — standard posting</option>
          <option value="team_admin">Workgroup Admin — manages this workgroup</option>
          <option value="billing_admin">Billing Admin — payment + seats only</option>
          <option value="full_admin">Super Admin — owner-level, creates workgroups</option>
        </select>

        {role === "full_admin" && (
          <div style={{ marginTop: 12, padding: 12, background: BG, borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Breakoff permission</div>
            {[
              { val: "team", label: "Can break off as Team" },
              { val: "agency", label: "Can break off as Agency" },
              { val: "none", label: "No breakoff" },
            ].map(opt => (
              <label key={opt.val} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 13, cursor: "pointer" }}>
                <input type="radio" name="breakoff" value={opt.val} checked={breakoff === opt.val} onChange={e => setBreakoff(e.target.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        )}

        {role !== detail.role && (
          <button onClick={saveRole} disabled={savingRole} style={{ marginTop: 12, background: G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", opacity: savingRole ? 0.6 : 1 }}>
            {savingRole ? "Saving..." : "Save role"}
          </button>
        )}
      </div>

      {/* Last 5 posts */}
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Last 5 posts</div>
        {(detail.recent_posts || []).length === 0 && (
          <div style={{ fontSize: 13, color: "#888" }}>No posts yet</div>
        )}
        {(detail.recent_posts || []).map((p, i) => {
          const date = p.scheduled_time ? new Date(p.scheduled_time).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
          const caption = (p.caption || "").substring(0, 80) + ((p.caption || "").length > 80 ? "..." : "");
          const isLive = p.status === "posted";
          return (
            <div key={p.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < (detail.recent_posts || []).length - 1 ? "0.5px solid " + BORDER : "none" }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: "#888" }}>{date}</span>
                <span style={{ fontSize: 12, color: "#888", margin: "0 6px" }}>·</span>
                <span style={{ fontSize: 13 }}>{caption}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: isLive ? GRN : AMB, background: isLive ? "#F0FAF4" : "#FFFBEB", padding: "2px 8px", borderRadius: 12 }}>
                  {isLive ? "Live" : "Pending"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Past posts (monthly accordion) */}
      {monthKeys.length > 0 && (
        <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Past posts</div>
          {monthKeys.map(key => {
            const posts = detail.monthly_posts[key] || [];
            const [year, month] = key.split("-");
            const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
            const isOpen = expandedMonth === key;
            return (
              <div key={key} style={{ borderBottom: "0.5px solid " + BORDER }}>
                <button
                  onClick={() => setExpandedMonth(isOpen ? null : key)}
                  style={{ width: "100%", background: "transparent", border: "none", padding: "12px 0", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{monthName} · {posts.length} post{posts.length !== 1 ? "s" : ""}</span>
                  <span style={{ fontSize: 12, color: "#888" }}>{isOpen ? "▴" : "▾"}</span>
                </button>
                {isOpen && posts.slice(0, 20).map((p, i) => {
                  const date = p.scheduled_time ? new Date(p.scheduled_time).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
                  const caption = (p.caption || "").substring(0, 80) + ((p.caption || "").length > 80 ? "..." : "");
                  return (
                    <div key={p.id || i} style={{ padding: "8px 0 8px 16px", fontSize: 13, color: "#555" }}>
                      <span style={{ color: "#888" }}>{date}</span> · {caption}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Plug status */}
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Plug status</div>
        <div style={{ fontSize: 14, color: plugColor, fontWeight: 500 }}>{plugLabel}</div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <button onClick={handleSuspend} disabled={suspending} style={{ background: AMB, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, opacity: suspending ? 0.6 : 1 }}>
          {suspending ? "Suspending..." : "Suspend"}
        </button>
        <button onClick={handleRemove} disabled={removing} style={{ background: RED, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, opacity: removing ? 0.6 : 1 }}>
          {removing ? "Removing..." : "Remove from workgroup"}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MEMBER VIEW (non-admin)
   ═══════════════════════════════════════════════════════════════════════════ */

function PlugPostSection({ plug, workspaceId, onReload }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/my-plug/" + plug.id, {
        method: "PATCH", headers, body: JSON.stringify({ personalized_caption: editText }),
      });
      if (r.ok) { setEditing(false); onReload(); }
      else { const d = await r.json(); alert(d.error || "Failed to save"); }
    } catch { alert("Error saving plug post"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/my-plug/" + plug.id, { method: "DELETE", headers });
      if (r.ok) { setConfirmDelete(false); onReload(); }
      else { const d = await r.json(); alert(d.error || "Failed to delete"); }
    } catch { alert("Error deleting plug post"); }
    setDeleting(false);
  };

  const hasPlug = plug && plug.status !== "deleted";
  const isDeleted = plug && plug.status === "deleted";
  const caption = plug ? (plug.personalized_caption || plug.ai_rewritten || "") : "";

  return (
    <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 16, marginBottom: 28 }}>
      <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Your plug post</div>
      {hasPlug && !editing && (
        <>
          <div style={{ fontSize: 13, color: "#333", lineHeight: 1.5, marginBottom: 12, whiteSpace: "pre-wrap" }}>{caption}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setEditText(caption); setEditing(true); }} style={{ background: "transparent", color: G, border: "0.5px solid " + G, borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Edit my version</button>
            <button onClick={() => setConfirmDelete(true)} style={{ background: "transparent", color: RED, border: "1px solid rgba(216,90,48,0.25)", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
          </div>
        </>
      )}
      {hasPlug && editing && (
        <>
          <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={5} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 10 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditing(false)} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>{saving ? "Saving..." : "Save"}</button>
          </div>
        </>
      )}
      {isDeleted && <div style={{ fontSize: 13, color: "#888" }}>You removed this week's plug post</div>}
      {!plug && <div style={{ fontSize: 13, color: "#888" }}>No plug post this week</div>}

      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 380, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Remove plug post?</div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 20, lineHeight: 1.5 }}>Your admin will be notified.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDelete(false)} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ background: RED, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", opacity: deleting ? 0.6 : 1 }}>{deleting ? "Removing..." : "Remove"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeaveModal({ workspaceName, workspaceId, onClose }) {
  const [feedback, setFeedback] = useState("");
  const [leaving, setLeaving] = useState(false);
  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/members/leave", {
        method: "POST", headers, body: JSON.stringify({ feedback: feedback.trim() }),
      });
      if (r.ok) { window.location.hash = "/team"; window.location.reload(); }
      else { const d = await r.json(); alert(d.error || "Failed to leave workspace"); setLeaving(false); }
    } catch { alert("Error leaving workspace"); setLeaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Leave {workspaceName}?</div>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 16, lineHeight: 1.5 }}>You'll lose access to team features. Your posts and content will transfer to your personal account.</div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: "#888", display: "block", marginBottom: 4 }}>Help us improve (optional)</label>
          <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3} placeholder="Any feedback..." style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleLeave} disabled={leaving} style={{ background: RED, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", opacity: leaving ? 0.6 : 1 }}>{leaving ? "Leaving..." : "Leave workspace"}</button>
        </div>
      </div>
    </div>
  );
}

function MemberView({ data, currentUserId, workspaceId, onReload }) {
  const ws = data.workspace;
  const members = data.members || [];
  const me = members.find(m => m.user_id === currentUserId) || {};
  const [showLeave, setShowLeave] = useState(false);

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 12, color: "#999", marginBottom: 2 }}>{ws.name}</div>
        <div style={{ fontSize: 22, fontWeight: 500 }}>{ws.name}</div>
      </div>

      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
        <StatCard label="Your posts this week" value={me.posts_this_week || 0} sub={(me.approved_this_week || 0) + " approved"} subColor={GRN} />
        <StatCard label="Your views (30d)" value={fmtNum(me.est_impressions_30d || 0)} />
        <StatCard label="Team members" value={members.length} />
      </div>

      {/* Team members grid */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Team members</div>
        <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {data.owner_info && (
            <div style={{ background: "#FFF8ED", border: "1px solid #F5A623", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
              <Avatar name={data.owner_info.name} src={data.owner_info.avatar} size={44} />
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 500 }}>{data.owner_info.name}</div>
              <div style={{ marginTop: 4 }}><span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#FEF3C7", color: "#92400E" }}>Owner</span></div>
            </div>
          )}
          {members.map(m => {
            const isMe = m.user_id === currentUserId;
            return (
              <div key={m.user_id} style={{ background: CARD, border: isMe ? "1.5px solid " + G : "0.5px solid " + BORDER, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", position: "relative" }}>
                {isMe && (
                  <span style={{ position: "absolute", top: 8, right: 8, background: "#E6F1FB", color: G, borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>You</span>
                )}
                <Avatar name={m.name} src={m.avatar} size={44} />
                <div style={{ marginTop: 10, fontSize: 14, fontWeight: 500 }}>{m.name}</div>
                <div style={{ marginTop: 4 }}><RoleBadge role={m.role} /></div>
              </div>
            );
          })}
        </div>
      </div>

      <PlugPostSection plug={data.my_plug} workspaceId={workspaceId} onReload={onReload} />

      {data.admin_name && (
        <div style={{ marginTop: 28, padding: "12px 16px", background: BG, borderRadius: 8, fontSize: 12, color: "#888" }}>
          Workspace managed by {data.admin_name}
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: "center" }}>
        <button onClick={() => setShowLeave(true)} style={{ background: "transparent", color: RED, border: "none", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Leave workspace</button>
      </div>

      {showLeave && (
        <LeaveModal workspaceName={ws.name} workspaceId={workspaceId} onClose={() => setShowLeave(false)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   INVITE MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

function InviteModal({ workspaceId, seatCost = 149, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);
  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const send = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/invite", {
        method: "POST", headers, body: JSON.stringify({ email: email.trim(), role }),
      });
      const d = await r.json();
      if (r.ok) { onDone(); }
      else alert(d.error || "Failed to send invite");
    } catch { alert("Error sending invite"); }
    setSending(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Invite team member</div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 4 }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="colleague@company.com" style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 4 }}>Role</label>
          <select value={role} onChange={e => setRole(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14 }}>
            <option value="member">Member</option>
            <option value="billing_admin">Billing Admin</option>
          </select>
        </div>
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>
          Sending this invite adds a seat (${seatCost}/month) to your subscription. If not accepted within 30 days the seat is released, but the first month is non-refundable.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={send} disabled={sending || !email.trim()} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", opacity: (sending || !email.trim()) ? 0.6 : 1 }}>
            {sending ? "Sending..." : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BULK INVITE (CSV) MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

function BulkInviteModal({ workspaceId, seatCost = 149, onClose, onDone }) {
  const [parsed, setParsed] = useState([]);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    setResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 1) { setError("File is empty"); return; }

      // Detect header row
      const first = lines[0].toLowerCase();
      const hasHeader = first.includes("email") || first.includes("name") || first.includes("role");
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const members = [];
      const seen = new Set();
      for (const line of dataLines) {
        const cols = line.split(",").map(c => c.trim().replace(/^["']|["']$/g, ""));
        const email = cols.find(c => c.includes("@"));
        if (!email) continue;
        const emailLower = email.toLowerCase();
        if (seen.has(emailLower)) continue;
        seen.add(emailLower);

        // Try to detect role column
        const roleCol = cols.find(c => ["member", "billing_admin", "team_admin", "full_admin"].includes(c.toLowerCase()));
        members.push({ email: emailLower, role: roleCol ? roleCol.toLowerCase() : "member" });
      }

      if (members.length === 0) { setError("No valid email addresses found in CSV"); return; }
      setParsed(members);
    };
    reader.readAsText(file);
  };

  const handleSend = async () => {
    setSending(true);
    setProgress(0);
    const successes = [];
    const failures = [];

    for (let i = 0; i < parsed.length; i++) {
      const m = parsed[i];
      try {
        const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/invite", {
          method: "POST", headers, body: JSON.stringify({ email: m.email, role: m.role }),
        });
        const d = await r.json();
        if (r.ok) successes.push(m.email);
        else failures.push({ email: m.email, error: d.error || "Failed" });
      } catch {
        failures.push({ email: m.email, error: "Network error" });
      }
      setProgress(i + 1);
    }

    setResults({ successes, failures });
    setSending(false);
  };

  const totalCharge = parsed.length * seatCost;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 520, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Bulk invite from CSV</div>

        {!results ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 8 }}>Upload a CSV file with email addresses</label>
              <input type="file" accept=".csv,.txt" onChange={handleFile} style={{ fontSize: 13 }} />
              <div style={{ fontSize: 11, color: "#999", marginTop: 6, lineHeight: 1.5 }}>
                CSV format: one email per row, or columns with email and optional role (member, billing_admin). Header row is auto-detected.
              </div>
            </div>

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#991B1B" }}>
                {error}
              </div>
            )}

            {parsed.length > 0 && (
              <>
                <div style={{ border: "1px solid #e0dfdc", borderRadius: 8, overflow: "hidden", marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8f8f6" }}>
                        <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#666", borderBottom: "1px solid #e0dfdc" }}>#</th>
                        <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#666", borderBottom: "1px solid #e0dfdc" }}>Email</th>
                        <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#666", borderBottom: "1px solid #e0dfdc" }}>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.map((m, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0ee" }}>
                          <td style={{ padding: "6px 12px", color: "#999" }}>{i + 1}</td>
                          <td style={{ padding: "6px 12px" }}>{m.email}</td>
                          <td style={{ padding: "6px 12px", color: "#666" }}>{m.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>
                    {parsed.length} member{parsed.length !== 1 ? "s" : ""} will be invited
                  </div>
                  <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>
                    Initial charge: <strong>${totalCharge}</strong> ({parsed.length} x ${seatCost}/seat).
                    Each seat is charged immediately. If not accepted within 30 days, the seat is released but the first month is non-refundable.
                  </div>
                </div>

                {sending && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ background: "#e8e8e4", borderRadius: 8, height: 8, overflow: "hidden" }}>
                      <div style={{ background: G, height: "100%", borderRadius: 8, width: `${Math.round(progress / parsed.length * 100)}%`, transition: "width 0.3s" }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4, textAlign: "center" }}>Sending {progress} of {parsed.length}...</div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={onClose} disabled={sending} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              {parsed.length > 0 && (
                <button onClick={handleSend} disabled={sending} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontWeight: 600, opacity: sending ? 0.6 : 1 }}>
                  {sending ? `Sending ${progress}/${parsed.length}...` : `Invite ${parsed.length} member${parsed.length !== 1 ? "s" : ""} ($${totalCharge})`}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              {results.successes.length > 0 && (
                <div style={{ background: "#E1F5EE", border: "1px solid #A8DCCA", borderRadius: 8, padding: "12px 14px", marginBottom: 8, fontSize: 13, color: "#0F6E56" }}>
                  {results.successes.length} invitation{results.successes.length !== 1 ? "s" : ""} sent successfully
                </div>
              )}
              {results.failures.length > 0 && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#991B1B" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{results.failures.length} failed:</div>
                  {results.failures.map((f, i) => (
                    <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>{f.email} — {f.error}</div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => { onDone(); }} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CREATE WORKGROUP MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

function CreateWorkgroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const st = localStorage.getItem("nectar_st");

  const handleCreate = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    setCreating(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Token": st },
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      const d = await r.json();
      if (r.ok && d.workspace_id) {
        onCreated(d.workspace_id, name.trim());
      } else {
        setError(d.error || "Failed to create workgroup");
      }
    } catch {
      setError("Network error");
    }
    setCreating(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Create workgroup</div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 4 }}>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Marketing Team" autoFocus
            style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: "#666", display: "block", marginBottom: 4 }}>Description (optional)</label>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What this workgroup does"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }} />
        </div>
        {error && <div style={{ fontSize: 13, color: RED, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleCreate} disabled={creating || !name.trim()}
            style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", opacity: (creating || !name.trim()) ? 0.6 : 1 }}>
            {creating ? "Creating…" : "Create workgroup"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — Router
   ═══════════════════════════════════════════════════════════════════════════ */

export default function WorkspaceDashboard({ workspaceId, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("overview"); // overview | workgroup | member
  const [activeWsId, setActiveWsId] = useState(workspaceId); // tracks which workgroup is open
  const [selectedMember, setSelectedMember] = useState(null);
  const hashAction = window.location.hash.includes("action=invite");
  const [showInvite, setShowInvite] = useState(hashAction);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [showCreateWg, setShowCreateWg] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState("");

  const st = localStorage.getItem("nectar_st");
  const headers = { "Content-Type": "application/json", "X-Session-Token": st };

  const load = useCallback(async (wsId) => {
    const targetWs = wsId || activeWsId;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + targetWs + "/team", { headers });
      if (r.ok) {
        setData(await r.json());
      } else {
        const d = await r.json();
        setError(d.error || "Failed to load team");
      }
    } catch {
      setError("Network error");
    }
    setLoading(false);
  }, [activeWsId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDeleteWorkgroup = async (wg) => {
    setDeleting(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${wg.id}`, { method: "DELETE", headers });
      const d = await r.json();
      if (r.ok) {
        setDeleteTarget(null);
        // Navigate away from the deleted workspace URL before reload
        window.location.hash = "/team";
        window.location.reload();
      } else {
        setToast(d.error || "Failed to delete");
      }
    } catch {
      setToast("Network error");
    }
    setDeleting(false);
  };

  const handleTransferOwnership = async () => {
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/transfer-ownership`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Token": localStorage.getItem("nectar_st") },
        body: JSON.stringify({ new_owner_user_id: parseInt(newOwnerId, 10) }),
      });
      const d = await r.json();
      if (r.ok) {
        setToast("Ownership transferred successfully");
        setTimeout(() => setToast(null), 4000);
        setShowTransferModal(false);
        setNewOwnerId("");
        load();
      } else {
        alert(d.error || "Failed to transfer ownership");
      }
    } catch { alert("Network error"); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading team...</div>;
  if (error) return <div style={{ padding: 40, textAlign: "center", color: RED }}>{error}</div>;
  if (!data) return null;

  const isAdmin = data.caller_role === "full_admin";

  const wsStatus = data?.workspace?.status;
  const paymentFailed = data?.workspace?.payment_failed_at;
  const suspendedAt = data?.workspace?.suspended_at;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px 48px" }}>
      {wsStatus === "suspended" && (
        <div style={{ background: "#FEF3CD", border: "1px solid #F5C518", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#856404", marginBottom: 6 }}>Workspace Suspended</div>
          <div style={{ fontSize: 14, color: "#856404", lineHeight: 1.5 }}>
            {isAdmin
              ? "Your workspace is suspended due to a payment issue. Publishing is paused and scheduled posts have been held. Please update your payment method to restore service."
              : "This workspace is currently suspended due to a billing issue. Your admin has been notified. Publishing is paused until the issue is resolved."}
          </div>
          {isAdmin && (
            <a href="#/settings?tab=billing" style={{ display: "inline-block", marginTop: 10, padding: "8px 18px", background: "#856404", color: "#fff", borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              Update payment &rarr;
            </a>
          )}
        </div>
      )}
      {wsStatus === "terminated" && (
        <div style={{ background: "#F8D7DA", border: "1px solid #F5C6CB", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#721C24", marginBottom: 6 }}>Workspace Closed</div>
          <div style={{ fontSize: 14, color: "#721C24", lineHeight: 1.5 }}>
            This workspace has been permanently closed due to extended payment failure. All members have been removed and restored to their previous plans.
          </div>
        </div>
      )}
      {wsStatus === "active" && paymentFailed && !suspendedAt && (
        <div style={{ background: "#FFF3CD", border: "1px solid #FFEEBA", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#856404", marginBottom: 6 }}>Payment Issue Detected</div>
          <div style={{ fontSize: 14, color: "#856404", lineHeight: 1.5 }}>
            {isAdmin
              ? "A recent payment has failed. Please update your payment method within 72 hours to avoid workspace suspension."
              : "Your team admin has been notified of a billing issue. No action needed from you at this time."}
          </div>
          {isAdmin && (
            <a href="#/settings?tab=billing" style={{ display: "inline-block", marginTop: 10, padding: "8px 18px", background: "#856404", color: "#fff", borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              Update payment &rarr;
            </a>
          )}
        </div>
      )}
      {isAdmin ? (
        <>
          {view === "overview" && (
            <TeamOverview
              data={data}
              workspaceId={activeWsId}
              onOpenWorkgroup={(wgId) => {
                if (wgId) {
                  setActiveWsId(wgId);
                  setView("workgroup");
                  if (wgId !== activeWsId) load(wgId);
                }
              }}
              onCreateWorkgroup={() => setShowCreateWg(true)}
              onDeleteWorkgroup={(wg) => setDeleteTarget(wg)}
              onTransferOwnership={() => setShowTransferModal(true)}
              onReload={load}
            />
          )}
          {view === "workgroup" && (
            <WorkgroupView
              data={data}
              workspaceId={activeWsId}
              onBack={() => { setActiveWsId(workspaceId); setView("overview"); if (activeWsId !== workspaceId) load(workspaceId); }}
              onViewMember={(m) => { setSelectedMember(m); setView("member"); }}
              onInvite={() => setShowInvite(true)}
              onBulkInvite={() => setShowBulkInvite(true)}
              onReload={() => load(activeWsId)}
            />
          )}
          {view === "member" && selectedMember && (
            <MemberDetail
              member={selectedMember}
              workspaceId={activeWsId}
              workspaceName={data.workspace.name}
              onBack={() => setView("workgroup")}
              onReload={() => load(activeWsId)}
            />
          )}
        </>
      ) : (
        <MemberView
          data={data}
          currentUserId={data.current_user_id}
          workspaceId={workspaceId}
          onReload={load}
        />
      )}

      {showInvite && (
        <InviteModal
          workspaceId={activeWsId}
          seatCost={data.monthly_cost_per_seat || 149}
          onClose={() => setShowInvite(false)}
          onDone={() => { setShowInvite(false); load(); }}
        />
      )}
      {showBulkInvite && (
        <BulkInviteModal
          workspaceId={activeWsId}
          seatCost={data.monthly_cost_per_seat || 149}
          onClose={() => setShowBulkInvite(false)}
          onDone={() => { setShowBulkInvite(false); load(); }}
        />
      )}
      {showCreateWg && (
        <CreateWorkgroupModal
          onClose={() => setShowCreateWg(false)}
          onCreated={(wsId, wsName) => {
            setShowCreateWg(false);
            load();
            setToast(`Workgroup "${wsName}" created successfully`);
            setTimeout(() => setToast(null), 4000);
          }}
        />
      )}
      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 400, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Delete workgroup</div>
            <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6, margin: "0 0 20px" }}>
              Are you sure you want to permanently delete <strong>{deleteTarget.name}</strong>?
              {deleteTarget.member_count > 1 && <><br /><span style={{ color: RED }}>Remove all members first.</span></>}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => handleDeleteWorkgroup(deleteTarget)} disabled={deleting || deleteTarget.member_count > 1}
                style={{ background: RED, color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", opacity: (deleting || deleteTarget.member_count > 1) ? 0.5 : 1 }}>
                {deleting ? "Deleting..." : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showTransferModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 480, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Transfer workspace ownership</div>
            <p style={{ fontSize: 14, color: "#444", lineHeight: 1.6, margin: "0 0 20px" }}>
              Choose a full admin member to transfer ownership to. You will remain a full admin afterward.
            </p>
            <select
              value={newOwnerId}
              onChange={(e) => setNewOwnerId(e.target.value)}
              style={{ width: "100%", padding: 10, fontSize: 14, border: "1px solid #ddd", borderRadius: 6, marginBottom: 20, boxSizing: "border-box" }}
            >
              <option value="">Select a member...</option>
              {(data?.members || [])
                .filter(m => m.role === "full_admin" && m.user_id !== data?.current_user_id)
                .map(m => (
                  <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                ))}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowTransferModal(false); setNewOwnerId(""); }}
                style={{ background: "transparent", border: "1px solid #ddd", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={handleTransferOwnership} disabled={!newOwnerId}
                style={{ background: newOwnerId ? "#0077B5" : "#ccc", color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: newOwnerId ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                Transfer ownership
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1D9E75", color: "#fff", padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
